/**
 * ClientDetailsModal Component
 * Modern client details modal inspired by Uber/Airbnb design patterns
 * Features: Smooth animations, comprehensive client info, appointment history
 */

import React, { useState, useEffect } from 'react';
import { 
  X, 
  User, 
  Phone, 
  Calendar, 
  MapPin, 
  Star, 
  Clock, 
  MessageCircle,
  ChevronRight,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { getAppointmentDetails, getClientInfo, getClientAppointmentHistory } from '../services/appointmentService';

const ClientDetailsModal = ({ isOpen, onClose, appointmentId }) => {
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [appointmentHistory, setAppointmentHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [error, setError] = useState(null);

  // Load client data when modal opens
  useEffect(() => {
    if (isOpen && appointmentId) {
      loadClientData();
    }
  }, [isOpen, appointmentId]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Debug: Log current user info for troubleshooting
      const { useAuthStore } = await import('../stores/authStore');
      const currentUser = useAuthStore.getState().user;
      
      // Get appointment details first
      const appointmentData = await getAppointmentDetails(appointmentId);
      setAppointmentDetails(appointmentData);
      
      if (appointmentData?.client?.id) {
        // Get full client info and history in parallel
        
        try {
          const clientInfo = await getClientInfo(appointmentData.client.id);
          setClientData(clientInfo);
        } catch (clientError) {
          setClientData(appointmentData.client);
        }
        
        try {
          const history = await getClientAppointmentHistory(appointmentData.client.id);
          setAppointmentHistory(history);
        } catch (historyError) {
          setAppointmentHistory([]);
        }
      } else if (appointmentData?.client) {
        // Use basic client info from appointment if detailed lookup fails
        setClientData(appointmentData.client);
        // Try to get history with basic client data
        try {
          const history = await getClientAppointmentHistory(appointmentData.client.id);
          setAppointmentHistory(history);
        } catch (historyError) {
          setAppointmentHistory([]);
        }
      }
    } catch (err) {
      setError('Não foi possível carregar os dados do cliente');
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format phone number
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  // Get status color and icon - Luxury Design (Square borders, monochromatic)
  const getStatusInfo = (status) => {
    const statusMap = {
      'SCHEDULED': { color: 'bg-transparent border border-blue-600/30 dark:border-blue-400/30 text-blue-600 dark:text-blue-400', icon: Calendar, label: 'Agendado' },
      'CONFIRMED': { color: 'bg-transparent border border-green-600/30 dark:border-green-400/30 text-green-600 dark:text-green-400', icon: CheckCircle, label: 'Confirmado' },
      'IN_PROGRESS': { color: 'bg-transparent border border-amber-600/30 dark:border-amber-400/30 text-amber-600 dark:text-amber-400', icon: Clock, label: 'Em Andamento' },
      'COMPLETED': { color: 'bg-transparent border border-brand-dark/30 dark:border-brand-light/30 text-brand-dark dark:text-brand-light', icon: CheckCircle, label: 'Concluído' },
      'CANCELLED': { color: 'bg-transparent border border-red-600/30 dark:border-red-400/30 text-red-600 dark:text-red-400', icon: XCircle, label: 'Cancelado' }
    };
    return statusMap[status] || statusMap['SCHEDULED'];
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Render loading state - Luxury Design
  const renderLoading = () => (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-brand-dark dark:border-brand-light border-t-transparent animate-spin mx-auto mb-4"></div>
        <p className="text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Carregando dados do cliente...</p>
      </div>
    </div>
  );

  // Render error state - Luxury Design
  const renderError = () => (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400 mx-auto mb-4" strokeWidth={1.5} />
        <p className="text-brand-dark/60 dark:text-brand-light/60 tracking-wide mb-4">{error}</p>
        <button
          onClick={loadClientData}
          className="px-6 py-3 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium uppercase tracking-widest text-sm hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
        >
          Tentar novamente
        </button>
      </div>
    </div>
  );

  // Render client info tab - Luxury Design
  const renderClientInfo = () => (
    <div className="space-y-6">
      {/* Client Profile Header - Square Design */}
      <div className="text-center pb-6 border-b border-brand-dark/10 dark:border-brand-light/10">
        <div className="w-20 h-20 bg-brand-dark/10 dark:bg-brand-light/10 border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl font-bold text-brand-dark dark:text-brand-light tracking-wide">
            {clientData?.full_name?.charAt(0)?.toUpperCase() || 'C'}
          </span>
        </div>
        <h2 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light mb-1 tracking-wide">
          {clientData?.full_name || clientData?.name || 'Cliente'}
        </h2>
        <p className="text-brand-dark/60 dark:text-brand-light/60 text-sm tracking-wide">
          {calculateAge(clientData?.date_of_birth) && `${calculateAge(clientData?.date_of_birth)} anos`}
        </p>
      </div>

      {/* Client Details - Square Design */}
      <div className="space-y-4">
        {/* Phone */}
        {(clientData?.phone_number || clientData?.phone) && (
          <div className="flex items-center space-x-4 p-4 bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20">
            <div className="w-10 h-10 bg-blue-600/10 dark:bg-blue-400/10 border border-blue-600/20 dark:border-blue-400/20 flex items-center justify-center">
              <Phone size={18} className="text-blue-600 dark:text-blue-400" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Telefone</p>
              <p className="font-medium text-brand-dark dark:text-brand-light tracking-wide">{formatPhoneNumber(clientData.phone_number || clientData.phone)}</p>
            </div>
            <button className="p-2 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-colors">
              <ChevronRight size={16} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
            </button>
          </div>
        )}

        {/* Date of Birth */}
        {clientData?.date_of_birth && (
          <div className="flex items-center space-x-4 p-4 bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20">
            <div className="w-10 h-10 bg-brand-dark/10 dark:bg-brand-light/10 border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center">
              <Calendar size={18} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Data de Nascimento</p>
              <p className="font-medium text-brand-dark dark:text-brand-light tracking-wide">{formatDate(clientData.date_of_birth)}</p>
            </div>
          </div>
        )}

        {/* Address */}
        {clientData?.address && (
          <div className="flex items-center space-x-4 p-4 bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20">
            <div className="w-10 h-10 bg-green-600/10 dark:bg-green-400/10 border border-green-600/20 dark:border-green-400/20 flex items-center justify-center">
              <MapPin size={18} className="text-green-600 dark:text-green-400" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Endereço</p>
              <p className="font-medium text-brand-dark dark:text-brand-light tracking-wide">{clientData.address}</p>
            </div>
          </div>
        )}

        {/* Email */}
        {clientData?.email && (
          <div className="flex items-center space-x-4 p-4 bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20">
            <div className="w-10 h-10 bg-amber-600/10 dark:bg-amber-400/10 border border-amber-600/20 dark:border-amber-400/20 flex items-center justify-center">
              <User size={18} className="text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Email</p>
              <p className="font-medium text-brand-dark dark:text-brand-light tracking-wide">{clientData.email}</p>
            </div>
          </div>
        )}
      </div>

      {/* Current Appointment Notes - Square Design */}
      {(appointmentDetails?.notes_by_client || appointmentDetails?.notes_by_professional) && (
        <div className="p-4 bg-blue-600/5 dark:bg-blue-400/5 border border-blue-600/20 dark:border-blue-400/20">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-blue-600/10 dark:bg-blue-400/10 border border-blue-600/20 dark:border-blue-400/20 flex items-center justify-center flex-shrink-0 mt-1">
              <MessageCircle size={16} className="text-blue-600 dark:text-blue-400" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 className="font-serif font-bold text-brand-dark dark:text-brand-light mb-2 tracking-wide">Observações do Agendamento</h3>
              {appointmentDetails.notes_by_client && (
                <div className="mb-2">
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-medium uppercase tracking-wider">Cliente:</p>
                  <p className="text-brand-dark dark:text-brand-light text-sm leading-relaxed tracking-wide">{appointmentDetails.notes_by_client}</p>
                </div>
              )}
              {appointmentDetails.notes_by_professional && (
                <div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-medium uppercase tracking-wider">Profissional:</p>
                  <p className="text-brand-dark dark:text-brand-light text-sm leading-relaxed tracking-wide">{appointmentDetails.notes_by_professional}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render appointment history tab - Luxury Design
  const renderAppointmentHistory = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">Histórico de Agendamentos</h3>
        <span className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">
          {appointmentHistory.length} agendamento{appointmentHistory.length !== 1 ? 's' : ''}
        </span>
      </div>

      {appointmentHistory.length === 0 ? (
        <div className="text-center py-8">
          <Calendar size={48} className="mx-auto mb-4 text-brand-dark/30 dark:text-brand-light/30" strokeWidth={1.5} />
          <p className="text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Nenhum agendamento anterior encontrado</p>
        </div>
      ) : (
        <div className="space-y-3">
          {appointmentHistory.map((appointment, index) => {
            const statusInfo = getStatusInfo(appointment.status);
            const StatusIcon = statusInfo.icon;

            return (
              <div
                key={appointment.id}
                className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-4 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                        {appointment.service?.name || 'Serviço'}
                      </h4>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium uppercase tracking-wider ${statusInfo.color}`}>
                        <StatusIcon size={12} className="mr-1" strokeWidth={1.5} />
                        {statusInfo.label}
                      </span>
                    </div>
                    <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">
                      {formatDate(appointment.appointment_date)} às {appointment.start_time}
                    </p>
                    {appointment.professional?.full_name && (
                      <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">
                        com {appointment.professional.full_name}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    {appointment.price_at_booking && (
                      <p className="font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                        R$ {Number(appointment.price_at_booking).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>

                {(appointment.notes_by_client || appointment.notes_by_professional) && (
                  <div className="mt-3 p-3 bg-brand-dark/5 dark:bg-brand-light/5 border border-brand-dark/10 dark:border-brand-light/10">
                    {appointment.notes_by_client && (
                      <div className="mb-2">
                        <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 font-medium uppercase tracking-wider">Observações do Cliente:</p>
                        <p className="text-sm text-brand-dark dark:text-brand-light tracking-wide">{appointment.notes_by_client}</p>
                      </div>
                    )}
                    {appointment.notes_by_professional && (
                      <div>
                        <p className="text-xs text-brand-dark/60 dark:text-brand-light/60 font-medium uppercase tracking-wider">Observações do Profissional:</p>
                        <p className="text-sm text-brand-dark dark:text-brand-light tracking-wide">{appointment.notes_by_professional}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop - Luxury Design */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 transition-opacity"
        onClick={onClose}
      />

      {/* Modal - Square Bottom Sheet */}
      <div className="absolute inset-x-0 bottom-0 bg-brand-light dark:bg-brand-dark border-t border-brand-dark/10 dark:border-brand-light/10 shadow-2xl transform transition-transform max-h-[90vh] flex flex-col">
        {/* Drag Handle - Square */}
        <div className="flex justify-center py-3">
          <div className="w-12 h-1 bg-brand-dark/30 dark:bg-brand-light/30" />
        </div>

        {/* Header - Luxury Design */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-dark/10 dark:border-brand-light/10 flex-shrink-0">
          <h1 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">Detalhes do Cliente</h1>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
          >
            <X size={20} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
          </button>
        </div>

        {/* Tabs - Square Design */}
        <div className="flex border-b border-brand-dark/10 dark:border-brand-light/10 px-6 flex-shrink-0">
          <button
            onClick={() => setActiveTab('info')}
            className={`px-4 py-3 font-medium text-sm uppercase tracking-wider border-b-2 transition-all duration-300 ${
              activeTab === 'info'
                ? 'border-brand-dark dark:border-brand-light text-brand-dark dark:text-brand-light'
                : 'border-transparent text-brand-dark/60 dark:text-brand-light/60 hover:text-brand-dark dark:hover:text-brand-light'
            }`}
          >
            Informações
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-3 font-medium text-sm uppercase tracking-wider border-b-2 transition-all duration-300 ${
              activeTab === 'history'
                ? 'border-brand-dark dark:border-brand-light text-brand-dark dark:text-brand-light'
                : 'border-transparent text-brand-dark/60 dark:text-brand-light/60 hover:text-brand-dark dark:hover:text-brand-light'
            }`}
          >
            Histórico
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 mobile-scroll">
          {loading ? renderLoading() : error ? renderError() : (
            activeTab === 'info' ? renderClientInfo() : renderAppointmentHistory()
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDetailsModal;